//#region src/pd-badge-order/DragController.ts
/** Threshold in px before drag starts on desktop */
var DRAG_THRESHOLD = 5;
/** Long-press duration in ms for touch drag */
var LONG_PRESS_MS = 300;
/**
* Reactive controller for pointer-based drag & drop.
* Supports mouse + touch via Pointer Events with setPointerCapture.
*
* Touch: Long-press on drag-handle starts drag; short tap falls through to badge-action.
* Desktop: Drag starts after DRAG_THRESHOLD px movement on drag-handle.
*/
var DragController = class {
	constructor(host) {
		this.isDragging = false;
		this.draggedId = null;
		this.sourceArea = null;
		this.ghostX = 0;
		this.ghostY = 0;
		this.dropTarget = null;
		this.insertIndex = 0;
		this._pointerId = null;
		this._startX = 0;
		this._startY = 0;
		this._longPressTimer = null;
		this._dragStarted = false;
		this._isTouch = false;
		this._ghostOffsetX = 0;
		this._ghostOffsetY = 0;
		this.onPointerDown = (e) => {
			if (e.button !== 0) return;
			const path = e.composedPath();
			if (path.some((el) => el instanceof HTMLElement && el.classList?.contains("action-zone"))) return;
			const badgeItem = path.find((el) => el instanceof HTMLElement && el.tagName === "PD-BADGE-ITEM");
			if (!badgeItem) return;
			if (badgeItem.hasAttribute("editing")) return;
			if (badgeItem.hasAttribute("disabled")) return;
			if (badgeItem.hasAttribute("readonly")) return;
			const badgeId = badgeItem.dataset.badgeId;
			if (!badgeId) return;
			const area = badgeItem.closest("[data-area]")?.dataset.area;
			if (area !== "active" && area !== "available") return;
			this._pointerId = e.pointerId;
			this._startX = e.clientX;
			this._startY = e.clientY;
			this._isTouch = e.pointerType === "touch";
			this._dragStarted = false;
			this.draggedId = badgeId;
			this.sourceArea = area;
			const rect = badgeItem.getBoundingClientRect();
			this._ghostOffsetX = e.clientX - rect.left;
			this._ghostOffsetY = e.clientY - rect.top;
			this._host.style.setProperty("--_ghost-width", `${rect.width}px`);
			if (this._isTouch) this._longPressTimer = setTimeout(() => {
				this._startDrag(e);
			}, LONG_PRESS_MS);
			e.currentTarget.setPointerCapture(e.pointerId);
			const target = e.currentTarget;
			target.addEventListener("pointermove", this._onPointerMove);
			target.addEventListener("pointerup", this._onPointerUp);
			target.addEventListener("pointercancel", this._onPointerCancel);
			e.preventDefault();
		};
		this._onPointerMove = (e) => {
			if (e.pointerId !== this._pointerId) return;
			const deltaX = e.clientX - this._startX;
			const deltaY = e.clientY - this._startY;
			if (!this._dragStarted) {
				const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
				if (this._isTouch) {
					if (distance > DRAG_THRESHOLD) {
						this._cancelLongPress();
						this._cleanup();
						return;
					}
				} else if (distance > DRAG_THRESHOLD) this._startDrag(e);
				else return;
			}
			if (!this._dragStarted) return;
			this.ghostX = e.clientX - this._ghostOffsetX;
			this.ghostY = e.clientY - this._ghostOffsetY;
			this._updateDropTarget(e.clientX, e.clientY);
			this._host.requestUpdate();
		};
		this._onPointerUp = (e) => {
			if (e.pointerId !== this._pointerId) return;
			this._cancelLongPress();
			if (this._dragStarted) this._executeDrop();
			this._cleanup();
			this._host.requestUpdate();
		};
		this._onPointerCancel = (e) => {
			if (e.pointerId !== this._pointerId) return;
			this._cancelLongPress();
			this._cleanup();
			this._host.requestUpdate();
		};
		this._host = host;
		host.addController(this);
	}
	hostConnected() {}
	hostDisconnected() {
		this._cleanup();
	}
	_startDrag(e) {
		this._cancelLongPress();
		this._dragStarted = true;
		this.isDragging = true;
		this.ghostX = e.clientX - this._ghostOffsetX;
		this.ghostY = e.clientY - this._ghostOffsetY;
		this.dropTarget = this.sourceArea;
		const ids = this._host.value || [];
		this.insertIndex = ids.indexOf(this.draggedId);
		this._host.requestUpdate();
	}
	_updateDropTarget(clientX, clientY) {
		const host = this._host;
		const activeList = host.shadowRoot?.querySelector("#active-list");
		const availableList = host.shadowRoot?.querySelector("#available-list");
		if (activeList) {
			const activeRect = activeList.getBoundingClientRect();
			if (clientX >= activeRect.left && clientX <= activeRect.right && clientY >= activeRect.top && clientY <= activeRect.bottom) {
				this.dropTarget = "active";
				this._calculateInsertIndex(activeList, clientY);
				return;
			}
		}
		if (availableList) {
			const availRect = availableList.getBoundingClientRect();
			if (clientX >= availRect.left && clientX <= availRect.right && clientY >= availRect.top && clientY <= availRect.bottom) {
				this.dropTarget = "available";
				this.insertIndex = 0;
				return;
			}
		}
		this.dropTarget = null;
	}
	_calculateInsertIndex(listEl, clientY) {
		const items = listEl.querySelectorAll("pd-badge-item");
		const ids = this._host.value || [];
		const dragIdx = ids.indexOf(this.draggedId);
		let visibleIdx = 0;
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			if (item.dataset.badgeId === this.draggedId) continue;
			const rect = item.getBoundingClientRect();
			if (clientY > rect.top + rect.height / 2) visibleIdx++;
			else break;
		}
		if (this.sourceArea === "active" && dragIdx >= 0) this.insertIndex = visibleIdx >= dragIdx ? visibleIdx + 1 : visibleIdx;
		else this.insertIndex = visibleIdx;
		this.insertIndex = Math.max(0, Math.min(this.insertIndex, ids.length));
	}
	_executeDrop() {
		const host = this._host;
		const ids = host.value || [];
		const draggedId = this.draggedId;
		if (this.dropTarget === "active" && this.sourceArea === "active") {
			const oldIndex = ids.indexOf(draggedId);
			if (oldIndex >= 0) {
				let newIndex = this.insertIndex;
				if (newIndex > oldIndex) newIndex = Math.min(newIndex - 1, ids.length - 1);
				if (oldIndex !== newIndex) host.reorderBadge(oldIndex, newIndex);
			}
		} else if (this.dropTarget === "available" && this.sourceArea === "active") host.deactivateBadge(draggedId);
		else if (this.dropTarget === "active" && this.sourceArea === "available") host.insertBadgeAt(draggedId, this.insertIndex);
	}
	_cancelLongPress() {
		if (this._longPressTimer !== null) {
			clearTimeout(this._longPressTimer);
			this._longPressTimer = null;
		}
	}
	_cleanup() {
		this._cancelLongPress();
		const shadow = this._host.shadowRoot;
		const lists = ["#active-list", "#available-list"].map((sel) => shadow?.querySelector(sel)).filter((el) => !!el);
		for (const listEl of lists) {
			listEl.removeEventListener("pointermove", this._onPointerMove);
			listEl.removeEventListener("pointerup", this._onPointerUp);
			listEl.removeEventListener("pointercancel", this._onPointerCancel);
			if (this._pointerId !== null) try {
				listEl.releasePointerCapture(this._pointerId);
			} catch {}
		}
		this.isDragging = false;
		this.draggedId = null;
		this.sourceArea = null;
		this.dropTarget = null;
		this.insertIndex = 0;
		this._pointerId = null;
		this._dragStarted = false;
	}
};
//#endregion
export { DragController };
